import React, { useState } from 'react';

import { Flex, Paper, Text, UnstyledButton, UnstyledButtonProps, useMantineTheme } from '@mantine/core';
import { useNavigate } from 'react-router';

export interface NavBarLinkButtonProps extends UnstyledButtonProps {
  link: {
    label: string;
    icon: React.ReactNode;
    value?: string;
    absolute?: boolean;
    target?: '_blank' | '_self' | '_parent' | '_top';
  };
  size?: 'sm' | 'md';
  isActive?: boolean;
  onActiveClickClb?: () => void;
  navBarOpened?: boolean;
  minimalHover?: boolean;
}

export function NavBarLinkButton({
  link,
  size = 'md',
  isActive,
  onActiveClickClb,
  navBarOpened,
  minimalHover,
  ...props
}: NavBarLinkButtonProps) {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    onActiveClickClb?.();

    // For internal links, we prevent the default behavior and navigate to the link value
    if (!link.absolute && link.value) {
      e.preventDefault();
      navigate(link.value);
    }
  };

  const padding = size === 'md' ? (navBarOpened ? '8px 10px' : '8px') : navBarOpened ? '6px 10px' : '8px';

  return (
    <UnstyledButton
      component="a"
      href={link.value}
      target={link.target}
      rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}>
      <Paper
        p={padding}
        sx={{
          border: `1px solid ${isActive ? theme.colors.gray[3] : 'transparent'}`,
          boxShadow: isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : undefined,
          transition: 'all 0.3s ease',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          color: isActive || (isHovered && minimalHover) ? theme.other.text.default : theme.other.text.dimmed,
          gap: 12,
          '&:hover': !minimalHover
            ? {
                zIndex: 100,
                transform: isActive ? undefined : 'scale(1.02)',
                transition: 'transform 0.2s ease, overflow 0.2s ease',
                backgroundColor: 'white',
                width: !navBarOpened ? 'fit-content' : undefined,
                border: !isActive ? `1px solid ${theme.other.gray.border}` : undefined
              }
            : undefined
        }}>
        <Flex direction="row" align="center" justify="center" style={{ flexShrink: 0, lineHeight: 1 }}>
          {link.icon}
        </Flex>
        {(navBarOpened || (isHovered && !minimalHover)) && (
          <Text size={size} style={{ whiteSpace: 'nowrap' }}>
            {link.label}
          </Text>
        )}
      </Paper>
    </UnstyledButton>
  );
}
